import { useState, useEffect } from "react";
import { TranslationService } from "../../../services/TranslationService";
import { ToastService } from "../../shared/bootstrap/Toast";
import CompanyPaymentProviderService from "../../../services/CompanyPaymentProviderService";
import { ProviderListItem } from "../../../entities/company/PaymentProvider/ProviderListItem";
import { CompanyProviderConfig } from "../../../entities/company/PaymentProvider/CompanyProviderConfig";
import { Field } from "../../../entities/company/PaymentProvider/Field";
import { FloatingPanelFooter } from "../../shared/components/FloatingPanelFooter";

interface PaymentProviderModalProps {
  availableProviders: ProviderListItem[];
  editingProvider: CompanyProviderConfig | null;
  onClose: () => void;
  onSaved: () => void;
}

const PaymentProviderModal = ({
  availableProviders,
  editingProvider,
  onClose,
  onSaved
}: PaymentProviderModalProps) => {
  const { translate } = TranslationService;
  const [saving, setSaving] = useState(false);
  const [selectedProviderName, setSelectedProviderName] = useState<string>(
    editingProvider?.PaymentProviderName ?? ""
  );
  const [providerFields, setProviderFields] = useState<Field[]>([]);
  const [formKey, setFormKey] = useState(0); // Key para forzar re-render del formulario
  const [originalPrivateKeyValue, setOriginalPrivateKeyValue] =
    useState<string>(""); // Guardar el valor original de private_key
  const [editedFields, setEditedFields] = useState<Set<string>>(new Set()); // Rastrear campos que han sido editados

  // Si solo hay un proveedor disponible y no estamos editando, seleccionarlo automáticamente
  useEffect(() => {
    if (
      !editingProvider &&
      availableProviders.length === 1 &&
      availableProviders[0]?.Name
    ) {
      const singleProviderName = availableProviders[0].Name;
      // Solo establecer si aún no está seleccionado (evita re-renders innecesarios)
      setSelectedProviderName((prev) =>
        prev !== singleProviderName ? singleProviderName : prev
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableProviders, editingProvider]);

  // Función para enmascarar el valor de private_key (solo últimos 6 caracteres visibles)
  const maskPrivateKey = (value: string): string => {
    if (!value || value.length <= 6) {
      return value;
    }
    const lastSix = value.slice(-6);
    const maskedPart = "*".repeat(value.length - 6);
    return maskedPart + lastSix;
  };

  // Función para obtener el valor a mostrar en el input
  const getDisplayValue = (field: Field): string => {
    // Solo enmascarar si es private_key, tiene valor original, y no ha sido editado
    if (
      field.Key === "private_key" &&
      originalPrivateKeyValue &&
      !editedFields.has(field.Key)
    ) {
      return maskPrivateKey(originalPrivateKeyValue);
    }
    return field.Value;
  };

  // Inicializar campos con labels del servicio cuando se monta el componente o cambia el proveedor
  // Esto refresca el formulario ante cada selección de proveedor
  useEffect(() => {
    if (selectedProviderName) {
      const selectedProvider = availableProviders.find(
        (p) => p.Name === selectedProviderName
      );
      if (selectedProvider) {
        if (
          editingProvider &&
          editingProvider.PaymentProviderName === selectedProviderName
        ) {
          // Al editar: combinar valores existentes con labels del servicio
          const fieldsWithLabels = selectedProvider.Fields.map(
            (serviceField) => {
              const existingField = editingProvider.ValuesConfig.find(
                (f) => f.Key === serviceField.Key
              );
              const value = existingField?.Value || "";

              // Si es private_key y tiene valor, guardar el valor original
              if (serviceField.Key === "private_key" && value) {
                setOriginalPrivateKeyValue(value);
              }

              return {
                Key: serviceField.Key,
                Value: value,
                Label: serviceField.Label || serviceField.Key
              };
            }
          );
          setProviderFields(fieldsWithLabels);
          // Resetear campos editados cuando se carga un proveedor
          setEditedFields(new Set());
        } else {
          // Al agregar o cambiar proveedor: refrescar formulario con labels del servicio y valores vacíos
          setProviderFields(
            selectedProvider.Fields.map((f) => ({
              Key: f.Key,
              Value: "",
              Label: f.Label || f.Key
            }))
          );
          setOriginalPrivateKeyValue("");
          setEditedFields(new Set());
        }
      }
    } else {
      setProviderFields([]);
      setOriginalPrivateKeyValue("");
      setEditedFields(new Set());
    }
  }, [selectedProviderName, availableProviders, editingProvider]);

  const handleProviderNameChange = (name: string) => {
    // Si se selecciona un proveedor diferente, limpiar completamente el formulario
    if (name !== selectedProviderName) {
      // Primero limpiar los campos para forzar el re-render completo
      setProviderFields([]);
      // Incrementar la key para forzar re-render del contenedor de campos
      setFormKey((prev) => prev + 1);
    }
    // Actualizar el nombre del proveedor, lo que disparará el useEffect para cargar los nuevos campos
    setSelectedProviderName(name);
  };

  const handleFieldFocus = (key: string) => {
    // Si es private_key y no ha sido editado, reemplazar el valor enmascarado con el real
    if (
      key === "private_key" &&
      !editedFields.has(key) &&
      originalPrivateKeyValue
    ) {
      setEditedFields((prev) => new Set(prev).add(key));
      setProviderFields((prev) =>
        prev.map((f) =>
          f.Key === key ? { ...f, Value: originalPrivateKeyValue } : f
        )
      );
    }
  };

  const handleFieldChange = (key: string, value: string) => {
    // Si es private_key y el usuario está editando por primera vez, marcar como editado
    if (key === "private_key" && !editedFields.has(key)) {
      setEditedFields((prev) => new Set(prev).add(key));
    }

    setProviderFields((prev) =>
      prev.map((f) => (f.Key === key ? { ...f, Value: value } : f))
    );
  };

  const handleSave = async () => {
    if (!selectedProviderName || providerFields.length === 0) {
      ToastService.showToast(
        "Por favor complete todos los campos requeridos",
        undefined,
        "warning"
      );
      return;
    }

    // Validar que todos los campos tengan valor
    const emptyFields = providerFields.filter(
      (f) => !f.Value || f.Value.trim() === ""
    );
    if (emptyFields.length > 0) {
      ToastService.showToast(
        "Por favor complete todos los campos requeridos",
        undefined,
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      const response = await CompanyPaymentProviderService.setProvider({
        PaymentLinkProvider: editingProvider?.PaymentLinkProviderId ?? null,
        providername: selectedProviderName,
        valuesconfig: providerFields
      });

      if (!(response instanceof Error)) {
        ToastService.showToast(
          editingProvider
            ? "Proveedor actualizado correctamente"
            : "Proveedor agregado correctamente",
          undefined,
          "success"
        );
        onSaved();
        onClose();
      } else {
        throw response;
      }
    } catch (e: any) {
      ToastService.showToast(
        e?.message ?? "Error al guardar el proveedor.",
        undefined,
        "danger"
      );
    } finally {
      setSaving(false);
    }
  };

  // Determinar si se debe mostrar el selector (solo si hay más de un proveedor disponible)
  const shouldShowProviderSelector = availableProviders.length > 1;

  return (
    <div className="floatingBody p-4">
      <div className="ps-3 d-flex h-100 flex-column">
        {/* Selector de Proveedor - Solo se muestra si hay más de un proveedor disponible */}
        {shouldShowProviderSelector && (
          <div className="form-group mb-3">
            <label className="form-label">Proveedor *</label>
            <select
              className="form-select"
              value={selectedProviderName}
              onChange={(e) => handleProviderNameChange(e.target.value)}
              disabled={!!editingProvider || saving}
            >
              <option value="">Selecciona un proveedor...</option>
              {availableProviders.map((provider) => (
                <option key={provider.Name} value={provider.Name}>
                  {provider.Name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Mostrar nombre del proveedor seleccionado cuando el selector está oculto */}
        {!shouldShowProviderSelector && selectedProviderName && (
          <div className="form-group mb-3">
            <label className="form-label">Proveedor</label>
            <div className="form-control-plaintext fw-bold">
              {selectedProviderName}
            </div>
          </div>
        )}

        {/* Campos dinámicos del proveedor */}
        {providerFields.length > 0 && (
          <div key={formKey} className="border-top pt-3">
            <h6 className="mb-3">Configuración del Proveedor</h6>
            {providerFields.map((field) => (
              <div key={field.Key} className="form-group mb-3">
                <label className="form-label">
                  {field.Label || field.Key} *
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={getDisplayValue(field)}
                  onFocus={() => handleFieldFocus(field.Key)}
                  onChange={(e) => handleFieldChange(field.Key, e.target.value)}
                  disabled={saving}
                  placeholder={`Ingrese ${field.Label || field.Key}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <FloatingPanelFooter>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onClose}
          disabled={saving}
        >
          {translate.Cancel}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving || !selectedProviderName}
        >
          {saving
            ? (translate.Saving ?? "Guardando...")
            : editingProvider
              ? translate.Update
              : translate.Save}
        </button>
      </FloatingPanelFooter>
    </div>
  );
};

export default PaymentProviderModal;
